import React from "react"
import FloatingActionButton from "material-ui/FloatingActionButton"
import ContentAdd from "material-ui/svg-icons/content/add"
import TableActionBar from "../../../components/layout/TableActionBar"
import { Paper, Table, TableBody, TableRow, TableRowColumn, CircularProgress, Toggle } from "material-ui"
import autobind from "class-autobind"
import strings from "../../../localization/strings"
import { IScheduleTemplate, ISchedulePatternTemplate } from "../../../models"
import AccountStore from "../../../stores/AccountStore"
import HierarchySettingsStore from "../../../stores/HierarchySettingsStore"
import * as hierarchyHelper from "../../../helpers/hierarchyHelper"
import ScheduleTemplatesTable from "./ScheduleTemplatesTable"
import AddScheduleTemplateDialog from "./AddScheduleTemplateDialog"
import * as AccountActions from "../../../actions/AccountActions"
import * as permissionHelper from "../../../helpers/permissionHelper"
import AuthenticationStore from "../../../stores/AuthenticationStore"
import AppStateStore from "../../../stores/AppStateStore"
import * as scheduleHelper from "../../../helpers/scheduleHelper"
import _ from "lodash"
import * as timeHelper from "../../../helpers/timeHelper"
import * as utils from "../../../helpers/utils"
import CopyScheduleDialog from "../../../components/schedule/copy_schedule/CopyScheduleDialog"
import * as jobHelper from "../../../helpers/jobHelper"

interface IState {
  showAllChildHierarchyData: boolean
  scheduleTemplates: IScheduleTemplate[]
  selectedScheduleTemplates: IScheduleTemplate[]
  searchText: string
  loading: boolean
  openAddScheduleTemplateDialog: boolean
  extendedScheduleTemplateId: string
  openCopyScheduleDialog: boolean
}

export default class ScheduleTemplates extends React.Component<any, IState> {
  constructor(props) {
    super(props)
    this.state = {
      showAllChildHierarchyData: true,
      scheduleTemplates: [],
      selectedScheduleTemplates: [],
      searchText: "",
      loading: false,
      openAddScheduleTemplateDialog: false,
      extendedScheduleTemplateId: null,
      openCopyScheduleDialog: false
    }
    autobind(this)
  }

  componentDidMount() {
    AccountStore.on("account_change", this.getScheduleTemplates)
    HierarchySettingsStore.on("change", this.getScheduleTemplates)
    this.getScheduleTemplates()
  }

  componentWillUnmount() {
    AccountStore.removeListener("account_change", this.getScheduleTemplates)
    HierarchySettingsStore.removeListener("change", this.getScheduleTemplates)
  }

  getScheduleTemplates() {
    if (!AccountStore.getCurrentAccount()) return
    const hierarchyIdsForShowAllChildHierarchyData = hierarchyHelper.getHierarchyIdsForShowAllChildHierarchyData(
      this.state.showAllChildHierarchyData,
      HierarchySettingsStore.getGlobalLevelItemFilterId()
    )
    const scheduleTemplates = AccountStore.getCurrentAccount().ScheduleData.ScheduleTemplates
      .filter((item) =>
        !item.Deleted &&
        (
          AccountStore.getJobsEnabled() ?
            jobHelper.getSupervisorAccessJobPermission(item.JobId) :
            true
        ) &&
        (hierarchyIdsForShowAllChildHierarchyData.find((id) => id === item.HierarchyId) === undefined ? false : true) &&
        (this.state.searchText ?
          item.Name.toLowerCase().includes(this.state.searchText.toLowerCase()) || item.Description.toLowerCase().includes(this.state.searchText.toLowerCase()) :
          true)
      )
      .sort((a, b) => {
        if (a.Name < b.Name) return -1
        if (a.Name > b.Name) return 1
        return 0
      })

    this.setState({
      loading: false,
      scheduleTemplates,
      selectedScheduleTemplates: [],
    })
  }

  handleAdd(scheduleTemplate) {
    this.setState({ openAddScheduleTemplateDialog: false, loading: true }, () => {
      AppStateStore.setProgressSnackbarContent("loading")
      AccountActions.addScheduleTemplate(AccountStore.getCurrentAccount().Id, scheduleTemplate, () =>
        AppStateStore.setProgressSnackbarContent(strings.Schedule_template_added_successfully)
      )
    })
  }

  handleCopyScheduleTemplates(scheduleTemplates: IScheduleTemplate[]) {
    this.setState({ openCopyScheduleDialog: false, loading: true }, () => {
      AppStateStore.setProgressSnackbarContent("loading")
      AccountActions.addScheduleTemplates(AccountStore.getCurrentAccount().Id, scheduleTemplates, () =>
        AppStateStore.setProgressSnackbarContent(strings.Schedule_template_copied_successfully)
      )
    })
  }

  handleDelete(scheduleTemplates: IScheduleTemplate[]) {
    this.setState({ loading: true }, () => {
      AppStateStore.setProgressSnackbarContent("loading")
      AccountActions.deleteScheduleTemplates(AccountStore.getCurrentAccount().Id, scheduleTemplates.map(item => item.Id), () =>
        AppStateStore.setProgressSnackbarContent(strings.Schedule_template_deleted_successfully)
      )
    })
  }

  handleAddPatternTemplates(scheduleTemplate: IScheduleTemplate, newPatternTemplates: ISchedulePatternTemplate[], days: number[]) {
    days.forEach(day => {
      const id = utils.guid()
      newPatternTemplates.forEach(newPatternTemplate => {
        newPatternTemplate.Id = id
        const updatedPatternTemplateForDay = scheduleHelper.updatePatternTemplateTimeForDay(timeHelper.keepConvertUTCToLocal(scheduleTemplate.StartDate), day, newPatternTemplate)
        scheduleTemplate.PatternTemplates.push(updatedPatternTemplateForDay)
      })
    })
    AppStateStore.setProgressSnackbarContent("loading")
    AccountActions.updateScheduleTemplate(AccountStore.getCurrentAccount().Id, scheduleTemplate, () =>
      AppStateStore.setProgressSnackbarContent(strings.Shift_added_successfully)
    )
  }

  handleDeletePatternTemplates(scheduleTemplate: IScheduleTemplate, selectedPatternTemplates: ISchedulePatternTemplate[]) {
    const selectedPatternTemplateIds = selectedPatternTemplates.map(i => i.Id)
    const updatedPatternTemplates: ISchedulePatternTemplate[] = []
    scheduleTemplate.PatternTemplates.forEach(pt =>
      !selectedPatternTemplateIds.includes(pt.Id) &&
      updatedPatternTemplates.push(pt)
    )

    scheduleTemplate.PatternTemplates = updatedPatternTemplates
    AppStateStore.setProgressSnackbarContent("loading")
    AccountActions.updateScheduleTemplate(AccountStore.getCurrentAccount().Id, scheduleTemplate, () =>
      AppStateStore.setProgressSnackbarContent(strings.Shift_deleted_successfully)
    )
  }

  handleUpdatePatternTemplateQty(scheduleTemplate: IScheduleTemplate, patternTemplate: ISchedulePatternTemplate, quantity: number) {
    const updatedPatternTemplates: ISchedulePatternTemplate[] = []
    scheduleTemplate.PatternTemplates.forEach(pt =>
      !_.isEqual(pt, patternTemplate) && updatedPatternTemplates.push(pt)
    )
    for (let i = 0; i < quantity; i++) {
      updatedPatternTemplates.push(patternTemplate)
    }
    scheduleTemplate.PatternTemplates = updatedPatternTemplates
    AppStateStore.setProgressSnackbarContent("loading")
    AccountActions.updateScheduleTemplate(AccountStore.getCurrentAccount().Id, scheduleTemplate, () =>
      AppStateStore.setProgressSnackbarContent(strings.Shift_quantity_updated_successfully)
    )
  }

  handleClonePatternTemplates(scheduleTemplate: IScheduleTemplate, clonePatternTemplates: ISchedulePatternTemplate[]) {
    const patternTemplates = _.cloneDeep(scheduleTemplate.PatternTemplates)
    scheduleTemplate.PatternTemplates = patternTemplates.concat(clonePatternTemplates)
    AppStateStore.setProgressSnackbarContent("loading")
    AccountActions.updateScheduleTemplate(AccountStore.getCurrentAccount().Id, scheduleTemplate, () =>
      AppStateStore.setProgressSnackbarContent(strings.Shifts_cloned_successfully)
    )
  }

  handleUpdateUniquePatternTemplate(scheduleTemplate: IScheduleTemplate, uniquePatternTemplate: ISchedulePatternTemplate) {
    scheduleTemplate.PatternTemplates = scheduleTemplate.PatternTemplates.map(i => {
      if (i.Id === uniquePatternTemplate.Id) {
        i = uniquePatternTemplate
      }
      return i
    })

    AppStateStore.setProgressSnackbarContent("loading")
    AccountActions.updateScheduleTemplate(AccountStore.getCurrentAccount().Id, scheduleTemplate, () =>
      AppStateStore.setProgressSnackbarContent(strings.Shift_updated_successfully)
    )
  }

  render() {
    return (
      <div className="page-container">
        <div className="page-container-parent">
          {permissionHelper.hasPermission(AuthenticationStore.Permissions.Schedule.AddAccountSchedule) &&
            <FloatingActionButton data-intercom-target={"menu-Schedules-ScheduleTemplates-btn-Add"} className="fixed-add" style={{ top: 83, right: 40, zIndex: 998 }} secondary={true} onTouchTap={() => this.setState({ openAddScheduleTemplateDialog: true })}>
              <ContentAdd />
            </FloatingActionButton>
          }
          <Paper zDepth={1} className="page-paper">
            <div style={{ width: "100%", marginTop: 20, display: "flex", justifyContent: "flex-end" }}>
              <Toggle
                data-intercom-target={"menu-Schedules-ScheduleTemplates-Toggle-ShowAllChildHierarchyData"}
                label={strings.Show_All_Child_Hierarchy_Data}
                style={{ width: 275, paddingRight: 35 }}
                labelStyle={{ fontSize: 15, fontWeight: 500 }}
                toggled={this.state.showAllChildHierarchyData}
                onToggle={(e, value) => this.setState({ showAllChildHierarchyData: value }, () => this.getScheduleTemplates())}
              />
            </div>
            <TableActionBar
              rowSelectionCount={this.state.selectedScheduleTemplates.length}
              singleSelectedMessage={strings.Schedule_Template_Selected}
              multipleSelectedMessage={strings.x_Schedule_Templates_Selected}
              title={strings.Schedule_Templates}
              onSearch={(searchText) => this.setState({ searchText }, () => this.getScheduleTemplates())}
              searchText={this.state.searchText}
              onDeleteClick={() =>
                AppStateStore.setConfirmDialogContent({
                  content: strings.Are_you_sure_you_want_to_delete_the_selected_schedule_template,
                  confirm: () => this.handleDelete(this.state.selectedScheduleTemplates)
                })
              }
              onCopyClick={permissionHelper.hasPermission(AuthenticationStore.Permissions.Schedule.AddAccountSchedule) &&
                (() => this.setState({ openCopyScheduleDialog: true }))
              }
              readonly={false}
            />
            <div style={{ padding: 20 }}>
              {this.state.loading ?
                <div className="spinner"><CircularProgress /></div> :
                <div>
                  {!this.state.scheduleTemplates.length ?
                    <Table>
                      <TableBody displayRowCheckbox={false}>
                        <TableRow>
                          <TableRowColumn>
                            <h2 style={{ textAlign: "center", marginTop: 25 }}>
                              {strings.No_schedule_template_found}
                            </h2>
                          </TableRowColumn>
                        </TableRow>
                      </TableBody>
                    </Table> :
                    <ScheduleTemplatesTable
                      scheduleTemplates={this.state.scheduleTemplates}
                      extendedScheduleTemplateId={this.state.extendedScheduleTemplateId}
                      handleSelect={(selectedScheduleTemplates) => this.setState({ selectedScheduleTemplates })}
                      handleUpdateExtendedId={(extendedScheduleTemplateId) => this.setState({ extendedScheduleTemplateId })}
                      handleAddPatternTemplates={this.handleAddPatternTemplates}
                      handleDeletePatternTemplates={this.handleDeletePatternTemplates}
                      handleUpdatePatternTemplateQty={this.handleUpdatePatternTemplateQty}
                      handleClonePatternTemplates={this.handleClonePatternTemplates}
                      handleUpdateUniquePatternTemplate={this.handleUpdateUniquePatternTemplate}
                      readonly={!permissionHelper.hasPermission(AuthenticationStore.Permissions.Schedule.DeleteAccountSchedule)}
                    />
                  }
                </div>
              }
            </div>
          </Paper>
        </div>
        {this.state.openAddScheduleTemplateDialog &&
          <AddScheduleTemplateDialog
            handleClose={() => this.setState({ openAddScheduleTemplateDialog: false })}
            handleAdd={this.handleAdd}
          />
        }
        {this.state.openCopyScheduleDialog &&
          <CopyScheduleDialog
            schedules={this.state.selectedScheduleTemplates}
            handleCopyScheduleTemplates={this.handleCopyScheduleTemplates}
            handleClose={() =>
              this.setState({ openCopyScheduleDialog: false, selectedScheduleTemplates: [] })
            }
            job={null}
            client={null}
          />
        }
      </div>
    )
  }
}